/**
 * Shared auth interceptor for axios clients.
 *
 * Dispatches a window-level `auth:expired` event on 401 responses so the
 * AuthContext can attempt a refresh token rotation. If that refresh fails,
 * AuthContext chains into `auth:logout-required`, which AuthListener catches
 * to toast + redirect to /login.
 *
 * Deliberately ignores 403. The backend currently returns 403 for legitimate
 * authorization failures (e.g. non-admin hitting /admin/* endpoints), and we
 * don't want those to kick off a logout loop.
 *
 * Also ignores the 401s that carry a Google reauth code (see
 * `GOOGLE_REAUTH_ERROR_CODES`): the BAO session is fine, only the Google
 * grant needs a new sign-in, which a session rotation cannot fix.
 */

import type { AxiosInstance, AxiosResponse } from "axios";

import { isGoogleReauthError } from "./googleAuthErrors";

interface AttachAuthInterceptorOptions {
    name?: string;
}

interface AxiosErrorLike {
    response?: {
        status?: number;
        data?: { detail?: string; [key: string]: unknown };
    };
    [key: string]: unknown;
}

/**
 * Best-effort string representation of an unknown error payload that doesn't
 * extend Error. Falls back to JSON.stringify so we don't end up with the
 * useless `[object Object]` produced by `String({...})`.
 */
export function stringifyUnknownError(err: unknown): string {
    if (typeof err === "string") return err;
    try {
        return JSON.stringify(err);
    } catch {
        return Object.prototype.toString.call(err);
    }
}

/**
 * Register the shared 401-handling response interceptor on an axios instance.
 *
 * @param client - The axios instance to augment.
 * @param options - Optional settings; `name` is the human label used in the dev warning (defaults to "API").
 */
export function attachAuthInterceptor(
    client: AxiosInstance,
    { name = "API" }: AttachAuthInterceptorOptions = {},
): void {
    client.interceptors.response.use(
        (res: AxiosResponse) => res,
        (err: AxiosErrorLike) => {
            if (err.response?.status === 401 && !isGoogleReauthError(err)) {
                if (process.env.NODE_ENV === "development") {
                    // eslint-disable-next-line no-console
                    console.warn(`[${name}] Session expirée détectée:`, err.response.status);
                }
                globalThis.dispatchEvent(
                    new CustomEvent("auth:expired", {
                        detail: {
                            status: err.response.status,
                            message: err.response.data?.detail || "Session expirée",
                            source: name,
                        },
                    }),
                );
            }
            // Best practice: Promise.reject should carry an Error so the
            // stack trace propagates. Axios errors always extend Error at
            // runtime; the fallback `new Error(...)` is a TypeScript-only
            // safety net for the AxiosErrorLike shape. Use a JSON-aware
            // stringifier so non-Error objects don't collapse to
            // `[object Object]`.
            return Promise.reject(
                err instanceof Error ? err : new Error(stringifyUnknownError(err)),
            );
        },
    );
}
